import { usePostApiV1SelectionsSelectionIdApplicantResume } from "@/api/recrutaAI/generated/aISaaSAPI";
import type {
	PostApiV1SelectionsSelectionIdApplicantResume200,
	PostApiV1SelectionsSelectionIdApplicantResumeBody,
} from "@/api/recrutaAI/generated/aISaaSAPI.schemas";
import { SAMPLE_RESUME_OPTIONS } from "@/constants/data/sampleResume/sampleResumeOptions";
import {
	Autocomplete,
	Button,
	Card,
	CloseButton,
	Group,
	Modal,
	Radio,
	SimpleGrid,
	Stack,
	Text,
	Textarea,
	rem,
	useMantineTheme,
} from "@mantine/core";
import { Dropzone, type FileWithPath } from "@mantine/dropzone";
import { useForm, zodResolver } from "@mantine/form";

import { notifications } from "@mantine/notifications";
import { IconFile, IconUpload, IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
	type AiDocumentSelectionForm,
	aiDocumentSelectionFormMethodSchema,
	aiDocumentSelectionFormSchema,
} from "../../schemas/aiDocumentSelectionForm";

const GENERATE_METHOD_OPTIONS = [
	{
		label: "サンプルレジュメから選択",
		value: aiDocumentSelectionFormMethodSchema.enum.dummyResume,
	},
	{
		label: "ファイルアップロード",
		value: aiDocumentSelectionFormMethodSchema.enum.fileUpload,
	},
	{
		label: "テキスト入力",
		value: aiDocumentSelectionFormMethodSchema.enum.textInput,
	},
] as const;

type Props = {
	selectionId: string;
	opened: boolean;
	close: () => void;
	recruiteeDetailDrawerOpen: () => void;
	setResume: (resume: PostApiV1SelectionsSelectionIdApplicantResume200) => void;
};

export function AiDocumentSelectionModal({
	selectionId,
	opened,
	close,
	recruiteeDetailDrawerOpen,
	setResume,
}: Props) {
	const theme = useMantineTheme();
	const [isSubmitting, setIsSubmitting] = useState(false);
	
	// Reset isSubmitting when modal is closed
	useEffect(() => {
		if (!opened) {
			setIsSubmitting(false);
		}
	}, [opened]);

	const aiDocumentSelectionForm = useForm<AiDocumentSelectionForm>({
		initialValues: {
			method: aiDocumentSelectionFormMethodSchema.enum.dummyResume,
		},
		validate: zodResolver(aiDocumentSelectionFormSchema),
	});

	const { mutate: postApiV1SelectionsSelectionIdApplicantResume } =
		usePostApiV1SelectionsSelectionIdApplicantResume({
			mutation: {
				onSuccess: (data) => {
					notifications.show({
						title: "AI書類選考が終了しました",
						message: "AI書類選考が終了しました",
						color: "green",
					});

					setResume(data);
				},
				onError: () => {
					notifications.show({
						title: "AI書類選考に失敗しました",
						message: "AI書類選考に失敗しました",
						color: "red",
					});
				},
			},
		});

	const renderPreview = () => {
		const values = aiDocumentSelectionForm.getValues();

		if (values.method !== aiDocumentSelectionFormMethodSchema.enum.fileUpload) {
			return null;
		}

		switch (values.file?.type) {
			case "application/pdf": {
				return (
					<Card key={values.file.name} withBorder padding="xs" pos="relative">
						<CloseButton
							size="sm"
							pos="absolute"
							right={5}
							top={5}
							radius="md"
							bg="#FFF"
							onClick={() => {
								aiDocumentSelectionForm.setFieldValue("file", undefined);
							}}
						/>
						<Stack align="center" gap="xs">
							<IconFile size={48} />
							<Text size="sm" ta="center">
								{values.file.name.length > 20
									? `${values.file.name.slice(0, 20)}...`
									: values.file.name}
							</Text>
						</Stack>
					</Card>
				);
			}
			default:
				return null;
		}
	};

	const renderGenerateMethodInputArea = () => {
		switch (aiDocumentSelectionForm.getValues().method) {
			case aiDocumentSelectionFormMethodSchema.enum.dummyResume:
				return (
					<Stack gap="xs">
						<Text size="sm" c="dimmed">
							職種を入力すると候補が表示されます
						</Text>
						<Autocomplete
							data={SAMPLE_RESUME_OPTIONS.map((option) => ({
								group: option.group,
								items: option.items.map((item) => ({
									// NOTE: mantineではvalueフィールドがラベルに利用される
									value: item.label,
								})),
							}))}
							placeholder="職種を入力"
							key={aiDocumentSelectionForm.key("label")}
							{...aiDocumentSelectionForm.getInputProps("label")}
						/>
					</Stack>
				);
			case aiDocumentSelectionFormMethodSchema.enum.fileUpload:
				return (
					<>
						<Dropzone
							// NOTE: 1つのみアップロード可能
							onDrop={(files) => {
								aiDocumentSelectionForm.setValues({
									file: files[0],
								});
							}}
							maxSize={3 * 1024 ** 2}
							accept={["application/pdf"]}
							style={{
								borderColor: aiDocumentSelectionForm.errors.file
									? theme.colors.red[6]
									: undefined,
								color: aiDocumentSelectionForm.errors.file
									? theme.colors.red[6]
									: undefined,
							}}
						>
							<Group
								justify="center"
								gap="xl"
								mih={240}
								style={{ pointerEvents: "none" }}
							>
								<Dropzone.Accept>
									<IconUpload
										style={{
											width: rem(52),
											height: rem(52),
											color: theme.colors.blue[6],
										}}
										stroke={1.5}
									/>
								</Dropzone.Accept>
								<Dropzone.Reject>
									<IconX
										style={{
											width: rem(52),
											height: rem(52),
											color: theme.colors.red[6],
										}}
										stroke={1.5}
									/>
								</Dropzone.Reject>
								<Dropzone.Idle>
									<IconFile
										style={{
											width: rem(52),
											height: rem(52),
											color: aiDocumentSelectionForm.errors.file
												? theme.colors.red[6]
												: theme.colors.dark[5],
										}}
										stroke={1.5}
									/>
								</Dropzone.Idle>

								<Stack gap="xs" style={{ maxWidth: "80%" }}>
									<Text size="xl" inline ta="center">
										クリックまたはドラッグ&ドロップで職務経歴書をアップロード
									</Text>
									<Text
										size="sm"
										c={aiDocumentSelectionForm.errors.file ? "red" : "dimmed"}
										inline
										ta="center"
									>
										対応ファイル形式 : .pdf(最大3MB)
									</Text>
								</Stack>
							</Group>
						</Dropzone>
						{aiDocumentSelectionForm.errors.file && (
							<Text size="sm" c="red" mt={-8}>
								{aiDocumentSelectionForm.errors.file}
							</Text>
						)}

						<SimpleGrid cols={{ base: 1, sm: 4 }}>{renderPreview()}</SimpleGrid>
					</>
				);
			case aiDocumentSelectionFormMethodSchema.enum.textInput:
				return (
					<Textarea
						placeholder="書類選考をする方のスキル・経歴等を入力してください。"
						minRows={12}
						autosize
						key={aiDocumentSelectionForm.key("textInput")}
						{...aiDocumentSelectionForm.getInputProps("textInput")}
					/>
				);
			default:
				return null;
		}
	};

	const handleFileToBase64 = async (file: FileWithPath): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const handleSubmit = async (values: AiDocumentSelectionForm) => {
		setIsSubmitting(true);
		let data: PostApiV1SelectionsSelectionIdApplicantResumeBody;

		try {
			switch (values.method) {
				case aiDocumentSelectionFormMethodSchema.enum.dummyResume: {
					const selectedValue = SAMPLE_RESUME_OPTIONS.flatMap((option) =>
						option.items.map((item) => ({
							label: item.label,
							value: item.value,
						})),
					).find((item) => item.label === values.label)?.value;

					data = {
						resume_text: selectedValue,
					};

					break;
				}
				case aiDocumentSelectionFormMethodSchema.enum.fileUpload: {
					const base64Image = await handleFileToBase64(
						values.file as FileWithPath,
					);

					data = {
						resume_base64: base64Image,
					};

					break;
				}
				case aiDocumentSelectionFormMethodSchema.enum.textInput: {
					data = {
						resume_text: values.textInput,
					};

					break;
				}
				default:
					throw new Error("Invalid method");
			}

			postApiV1SelectionsSelectionIdApplicantResume({
				selectionId,
				data,
			});

			notifications.show({
				title: "AI書類選考を開始しました",
				message: "AI書類選考を開始しました",
				color: "primary",
			});

			aiDocumentSelectionForm.reset();
			setIsSubmitting(false);
			close();
			recruiteeDetailDrawerOpen();
		} catch (error) {
			setIsSubmitting(false);
		}
	};



	return (
		<Modal.Root
			opened={opened}
			// biome-ignore lint/suspicious/noEmptyBlockStatements: <explanation>
			onClose={() => {
				setIsSubmitting(false);
			}}
			size="xl"
			radius="md"
			centered
		>
			<Modal.Overlay />
			<Modal.Content>
				<Modal.Header>
					<Modal.Title fz="xl">AI書類選考</Modal.Title>
					<Modal.CloseButton
						onClick={() => {
							aiDocumentSelectionForm.reset();
							setIsSubmitting(false);
							close();
						}}
					/>
				</Modal.Header>
				<Modal.Body p="lg">
					<form
						onSubmit={aiDocumentSelectionForm.onSubmit(handleSubmit)}
					>
						<Stack gap="lg">
							<Stack>
								<Text>
									職務経歴書をアップロードすることで、AIが書類選考を行います。
								</Text>
								<Stack>
									<Radio.Group
										name="generateMethod"
										label="職務経歴書"
										withAsterisk
										key={aiDocumentSelectionForm.key("method")}
										{...aiDocumentSelectionForm.getInputProps("method")}
									>
										<Group mt="xs">
											{GENERATE_METHOD_OPTIONS.map((option) => (
												<Radio
													key={option.value}
													value={option.value}
													label={option.label}
												/>
											))}
										</Group>
									</Radio.Group>
									{renderGenerateMethodInputArea()}
								</Stack>
							</Stack>
							<Group justify="flex-end">
								<Button
									type="submit"
									disabled={!aiDocumentSelectionForm.isDirty() || isSubmitting}
									loading={isSubmitting}
								>
									<Text>AI書類選考を開始</Text>
								</Button>
							</Group>
						</Stack>
					</form>
				</Modal.Body>
			</Modal.Content>
		</Modal.Root>
	);
}
